import { useState } from 'react';
import { Button, Card, cn } from '@wundo/ui';
import type {
  RoleplayMode,
  RoleplaySessionResponse,
  VoiceConnection,
} from '@wundo/shared/roleplay.ts';
import { PracticeChat } from './PracticeChat.tsx';
import { PracticeSessionBrief } from './PracticeSessionBrief.tsx';
import { PracticeVoiceCall, type PracticeVoiceCallProps } from './PracticeVoiceCall.tsx';
import { PracticeVoiceStage } from './PracticeVoiceStage.tsx';
import { m } from '@/paraglide/messages.js';

/** What the conversation is doing, once the session itself has loaded. */
export interface LiveConversation {
  mode: RoleplayMode;
  /** Null in a written conversation, and until a spoken one is granted a call. */
  voice: VoiceConnection | null;
}

/** What a spoken call needs from the screen around it. */
export type PracticeLiveCall = Pick<
  PracticeVoiceCallProps,
  'hangUpRef' | 'agentState' | 'onAgentState' | 'microphoneId' | 'speakerId'
>;

export interface PracticeSessionLiveProps {
  live: LiveConversation;
  session: RoleplaySessionResponse;
  call: PracticeLiveCall;
  /** The character has said in its own words that it is time to wrap up. */
  wrapUpSuggested: boolean;
  onSuggestEnd: () => void;
  ending: boolean;
  endFailed: boolean;
  onEnd: () => void;
}

/**
 * The conversation itself, beside the brief. The conversation comes first: on
 * a phone the brief above it pushed the orb off the bottom of the screen.
 */
export function PracticeSessionLive({
  live,
  session,
  call,
  wrapUpSuggested,
  onSuggestEnd,
  ending,
  endFailed,
  onEnd,
}: PracticeSessionLiveProps) {
  const [captionsVisible, setCaptionsVisible] = useState(true);

  return (
    <div
      className={cn(
        'min-h-0 flex-1 overflow-y-auto px-5 pt-6.5 agenda:overflow-hidden',
        /*
         * The dock floats over the bottom of the window rather than in the
         * column, so the column has to stop above it. Without this the
         * white card runs on underneath the controls.
         */
        live.mode === 'voice' ? 'pb-32.5' : 'pb-6.5',
      )}
    >
      {/*
        A flex row rather than a grid: a grid track grows with a long
        situation and carries the stage off the bottom of the window. In a
        flex row the height is the window's, whatever is written in it.
      */}
      <div className='mx-auto flex w-full max-w-240 flex-col gap-4.5 agenda:h-full agenda:flex-row agenda:items-stretch'>
        <div className='flex min-h-95 flex-col gap-3.5 agenda:order-2 agenda:min-h-0 agenda:flex-1'>
          {endFailed && (
            <p role='alert' className='text-base text-danger'>
              {m.practice_session_end_failed()}
            </p>
          )}

          {wrapUpSuggested && (
            <Card tone='accent' className='flex flex-wrap items-center gap-3.5'>
              <p className='min-w-0 flex-1 text-base text-ink'>
                {m.practice_session_suggest_end()}
              </p>
              <Button size='sm' disabled={ending} onClick={onEnd}>
                {m.practice_session_end()}
              </Button>
            </Card>
          )}

          <LiveStage
            live={live}
            sessionId={session.id}
            personaName={session.scenario.persona.name}
            onSuggestEnd={onSuggestEnd}
            call={{
              ...call,
              captionsVisible,
              leaving: ending,
              onCallEnded: () => {
                if (!ending) {
                  onEnd();
                }
              },
              onToggleCaptions: () => setCaptionsVisible((visible) => !visible),
            }}
          />
        </div>

        {/* 320px, the width the reference gives it. */}
        <div className='agenda:order-1 agenda:min-h-0 agenda:w-80 agenda:shrink-0 agenda:overflow-y-auto'>
          <PracticeSessionBrief scenario={session.scenario} />
        </div>
      </div>
    </div>
  );
}

function LiveStage({
  live,
  sessionId,
  personaName,
  onSuggestEnd,
  call,
}: {
  live: LiveConversation;
  sessionId: string;
  personaName: string;
  onSuggestEnd: () => void;
  call: Omit<PracticeVoiceCallProps, 'connection' | 'personaName' | 'onSuggestEnd'>;
}) {
  if (live.mode === 'chat') {
    return (
      <PracticeChat
        sessionId={sessionId}
        personaName={personaName}
        onSuggestEnd={onSuggestEnd}
      />
    );
  }
  if (live.voice === null) {
    /* A spoken conversation with no call to join yet: still connecting. */
    return (
      <PracticeVoiceStage
        personaName={personaName}
        agentState={null}
        captionsVisible={call.captionsVisible}
      />
    );
  }
  return (
    <PracticeVoiceCall
      {...call}
      connection={live.voice}
      personaName={personaName}
      onSuggestEnd={onSuggestEnd}
    />
  );
}
